// Vue core libraries - Pinia, VueUse & Vue
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";

import { Tag } from "@tmi/types/entities";

// Non-reactive variables
import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
import { refreshTagsFromServer } from "@/services/data/sync/tag-sync-coordinator";
import LocalForagePlugin, { tags } from "@/plugins/LocalForagePlugin";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

// State values
const initialized = computed< boolean >( () => allTags.value.length > 0 );
const loading = ref< boolean >( false );
const refreshing = ref< boolean >( false );

const defaultTags: Tag[] = [];
const allTags = ref< Tag[] >( defaultTags );

export const useTagsStore = defineStore( 'tags', () => {
    function reset(): void {
        allTags.value.splice( 0 );
        loading.value = false;
        refreshing.value = false;
    }

    async function initialize(): Promise< void > {
        if ( initialized.value || loading.value ) {
            logger.debug( '[ Tags ].initialize() exiting - already processing...' );
            return;
        }

        try {
            logger.debug( `[ Tags ] Checking the local database for any tags...` );

            loading.value = true;

            const t0: number = performance.now();

            if ( typeof tags === 'undefined' ) {
                // Wait for the driver to settle
                await LocalForagePlugin.initialize();
            }

            await tags.ready();
            const numberTags = await tags.length();

            const uniqueTags: Map< string, Tag > = new Map< string, Tag >();
            if ( numberTags > 0 ) {
                // Clear the contents of the backing store
                allTags.value.splice( 0 );

                // Iterate over all elements within IDB
                await tags.iterate( ( tag: Tag ) => {
                    uniqueTags.set( tag.id, tag );
                    return;
                } );

                allTags.value = [ ...( Array.from( uniqueTags.values() ) ) ];

                const t1: number = performance.now();
                logger.info( `(local) Loaded ${ allTags.value.length } tags in ${ t1 - t0 } ms` );
            } else {
                if ( refreshing.value ) {
                    return;
                }

                refreshing.value = true;

                // Timing - testing to see how expensive each method is
                const t0: number = performance.now();

                refreshTagsFromServer()
                    .then( ( hydratedTags: Tag[] ) => allTags.value = [ ...hydratedTags ] )
                    .catch( ( err: any ) => {
                        logger.error( JSON.stringify( err.message ) );
                        reset();
                    } )
                    .finally( () => {
                        // Clear the "loading" flag
                        loading.value = false;
                        refreshing.value = false;

                        const t1: number = performance.now();
                        logger.info( `Fetching & mapping ${ allTags.value.length } tag items took ${ t1 - t0 } ms` );
                    } );
            }
        } catch( err: any ) {
            logger.error( JSON.stringify( err.message ) )
            reset();
        } finally {
            // Clear the "loading" flag
            loading.value = false;
            refreshing.value = false;
        }
    }

    function tagById( sysId: string ) : Tag | undefined {
        return allTags.value.find( ( tag: Tag ): boolean => sysId === tag.id );
    }

    return {
        // ACTIONS
        initialize,
        reset,
        tagById,

        // GETTERS
        allTags
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useTagsStore, import.meta.hot ) );
}
